import { css, Interpolation, Theme } from '@emotion/react';
import { AnalyticsGatewayV1 } from '@frontend/api-analytics-gateway';
import { getUser } from '@frontend/auth-helpers';
import { useTranslation } from '@frontend/i18n';
import { useAppScopeStore } from '@frontend/scope';
import { useIsSmallMode } from '@frontend/shared';
import { sentry } from '@frontend/tracking';
import { theme } from '@frontend/theme';
import { useModalControl, useAlert, Button } from '@frontend/design-system';
import { GeneralFeedbackModal } from './general-feedback-modal';

type Props = {
  responsive?: boolean;
  containerStyles?: Interpolation<Theme>;
  variant?: 'text' | 'icon';
  selectedLocationFromPageFilter?: string[];
};

export const GeneralFeedbackButton = ({
  responsive = true,
  containerStyles,
  variant = 'text',
  selectedLocationFromPageFilter,
}: Props) => {
  const { t } = useTranslation('analytics');
  const alert = useAlert();
  const { modalProps, triggerProps } = useModalControl();
  const { selectedLocationIds, getLocationName } = useAppScopeStore();
  const { isSmallMode } = useIsSmallMode();
  const selectedLocationName = getLocationName(selectedLocationIds[0] ?? '');

  const { isLoading, mutateAsync: submitFeedback } = AnalyticsGatewayV1.Mutations.useSubmitFeedbackMutation({
    options: {
      onSuccess: () => {
        alert.success(t('Feedback submitted. Thank you!'));
        modalProps.onClose();
      },
      onError: (error) => {
        alert.error(t('Error submitting feedback. Please try again.'));
        sentry.warn({
          team: 'analytics',
          topic: 'analytics',
          error: `Failed to submit feedback: ${error instanceof Error ? error.message : String(error)}`,
        });
      },
    },
  });

  const handleSubmit = (message: string) => {
    if (selectedLocationIds.length === 0) {
      alert.error(t('Error submitting feedback. Please try again.'));
      return;
    }

    /*
      If there are multiple selected locations from the page filter, include them in the feedback. 
      Otherwise, include the single selected location.
    */
    const selected_locations = selectedLocationFromPageFilter?.length
      ? selectedLocationFromPageFilter
      : selectedLocationIds.length > 0
      ? [selectedLocationIds[0]]
      : [];

    submitFeedback({
      locationId: selectedLocationIds[0] || '',
      locationName: selectedLocationName,
      feedbackText: message,
      selectedLocations: selected_locations,
      analyticsPageUrl: window.location.href,
      userEmail: getUser()?.username || '',
    });
  };

  return (
    <>
      <div css={containerStyles}>
        {variant === 'icon' || (responsive && isSmallMode) ? (
          <Button
            iconName='user-voice'
            variant='secondary'
            {...triggerProps}
            css={styles.iconButton}
            aria-label={t('Submit Feedback')}
            hoverLabel={t('Submit Feedback')}
          />
        ) : (
          <Button
            variant='tertiary'
            iconName='star-small'
            css={styles.button}
            aria-label={t('Submit Feedback')}
            {...triggerProps}
          >
            {t('Submit Feedback')}
          </Button>
        )}
      </div>
      <GeneralFeedbackModal isLoading={isLoading} modalProps={modalProps} onSubmit={handleSubmit} />
    </>
  );
};

const styles = {
  button: css`
    svg {
      width: var(--fabric-space-gap-md);
      height: var(--fabric-space-gap-md);
    }
  `,
  iconButton: css`
    padding: ${theme.spacing(0.5)};
    svg {
      width: 24px;
      height: 24px;
    }
  `,
};
